/* istanbul ignore file */
import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import Webcam from 'react-webcam';
import { Button } from 'src/shared-components';
import Counter from './Counter';
import RecordTimer from './RecordTimer';

const VideoCapture = ({
  onVideoSent,
  width,
  height,
  timeLimit,
}: {
  onVideoSent: (blob: Blob) => void;
  width: number;
  height: number;
  timeLimit: number;
}) => {
  const webcamRef = useRef<Webcam & HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const playerRef = useRef<ReactPlayer>(null);
  const counterRef = useRef<any>(null); // TODO: set the right type intead of any
  const [playPreview, setPlayPreview] = useState<boolean>();
  const [canRecord, setCanRecord] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>();
  const [recordingFinished, setRecordingFinished] = useState<boolean>();
  const [showCountdown, setShowCountdown] = useState<boolean>();
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [videoUrl, setVideoUrl] = useState<string>();
  const [previewPlayed, setPreviewPlayed] = useState<number>(0);
  const [devices, setDevices] = React.useState([]);

  const [videoConstraints, setVideoConstraints] = useState<any>({
    facingMode: 'user',
    width,
    height,
  });

  const handleDevices = React.useCallback(
    mediaDevices => {
      const devicesList = mediaDevices.filter((device: any) => device.kind === 'videoinput');
      setDevices(devicesList); // In case we have to show the list to the user (not implemented yet)
      setVideoConstraints({ ...videoConstraints, deviceId: devicesList[0].deviceId });
      console.log(devices);
    },
    [setDevices],
  );

  React.useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const getUserMedia = () => {
    (window.navigator as any).getUserMedia(
      // constraints
      {
        video: true,
        audio: true,
      },
      // successCallback
      function (localMediaStream: any) {
        setCanRecord(true);
      },
      // errorCallback
      function (error: any) {
        // TODO: Explain why you need permission and how to update the permission setting
        console.log('err', error);
        setCanRecord(false);
      },
    );
  };

  const handleStartCapture = useCallback(() => {
    setIsRecording(true);
    counterRef.current?.action('start');
    const newStream = new MediaStream();
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current?.stream || newStream, {
      mimeType: 'video/webm;codecs=h264', // So it can be saved as *.mp4
    });
    mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
    mediaRecorderRef.current.start();
  }, [webcamRef, setIsRecording, mediaRecorderRef]);

  const handleDataAvailable = useCallback(
    ({ data }) => {
      if (data.size > 0) {
        setRecordedChunks(prev => prev.concat(data));
      }
    },
    [setRecordedChunks],
  );

  const handleStopCapture = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setRecordingFinished(true);
    counterRef.current?.action('pause');
  }, [mediaRecorderRef, setRecordingFinished]);

  useEffect(() => {
    // Generates the url so the preview can be played
    if (recordedChunks.length) {
      const blob = new Blob(recordedChunks, {
        type: 'video/mp4',
      });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    }
  }, [recordedChunks]);

  const handleSend = () => {
    const blob = new Blob(recordedChunks, {
      type: 'video/mp4',
    });
    onVideoSent(blob);
  };

  // const handleDownload = useCallback(() => {
  //   if (recordedChunks.length) {
  //     const blob = new Blob(recordedChunks, {
  //       type: 'video/mp4',
  //     });
  //     const url = URL.createObjectURL(blob);
  //     const a = document.createElement('a');
  //     document.body.appendChild(a);
  //     a.style.display = 'none';
  //     a.href = url;
  //     a.download = 'stream-capture.mp4';
  //     a.click();
  //     window.URL.revokeObjectURL(url);
  //     setRecordedChunks([]);
  //   }
  // }, [recordedChunks]);

  return (
    <div
      className="relative bg-gray-100 flex items-center justify-center"
      style={{ width: canRecord ? 'fit-content' : '100%', height: '100%' }}
    >
      {canRecord ? (
        <div className="w-fit flex relative justify-center items-center">
          {recordingFinished ? (
            <ReactPlayer
              url={videoUrl}
              ref={playerRef}
              playing={playPreview}
              width="100%"
              height="100%"
              onProgress={state => {
                setPreviewPlayed(state.playedSeconds);
                console.log(state.playedSeconds);
              }}
              onEnded={() => {
                counterRef.current?.action('pause');
                setPlayPreview(false);
              }}
            />
          ) : (
            <div className="w-full" style={{ height: '100%' }}>
              <Webcam audio={true} muted={true} videoConstraints={videoConstraints} ref={webcamRef} />
            </div>
          )}
          {(showCountdown || isRecording) && (
            <Counter maxSeconds={timeLimit} onMaxReached={handleStopCapture} ref={counterRef} />
          )}

          <div className="absolute bottom-0" style={{ height: 60, width: '100%', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            {isRecording ? (
              <>
                {recordingFinished ? (
                  <div className="flex">
                    <div className="flex-1 flex">
                      <div className="has-tooltip ml-2">
                        <button
                          onClick={() => {
                            if (previewPlayed > 0) {
                              counterRef.current?.action('pause');
                            } else {
                              playerRef.current?.seekTo(0);
                              counterRef.current?.action('restart');
                            }
                            setPlayPreview(!playPreview);
                          }}
                          className="h-10 w-10 mt-2 flex"
                        >
                          <span className="tooltip rounded shadow-lg px-3 py-1 bg-gray-600 text-white -mt-10 text-xs">
                            Play preview
                          </span>
                          <Image
                            src={`/images/icons/video/${playPreview ? 'pause' : 'play'}.svg`}
                            alt="Play preview"
                            width="100%"
                            height="100%"
                          />
                        </button>
                      </div>
                      <div className="has-tooltip ml-2">
                        <button
                          onClick={() => {
                            setIsRecording(false);
                            setRecordingFinished(false);
                            setRecordedChunks([]);
                          }}
                          className="h-10 w-10 mt-2 flex"
                        >
                          <span className="tooltip rounded shadow-lg px-3 py-1 bg-gray-600 text-white -mt-10 text-xs">
                            Record again
                          </span>
                          <Image src={'/images/icons/video/redo.svg'} alt="Play preview" width="100%" height="100%" />
                        </button>
                      </div>
                    </div>
                    <div className="pr-2">
                      {/* <Button
                        className="mt-2 mr-2"
                        onClick={() => {
                          handleDownload();
                        }}
                      >
                        Download
                      </Button> */}
                      <Button
                        className="mt-2"
                        color="white"
                        onClick={() => {
                          setRecordingFinished(true);
                          handleSend();
                        }}
                      >
                        Send
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="has-tooltip">
                    <button
                      onClick={handleStopCapture}
                      className="h-10 w-10 mt-2 flex justify-center items-center m-auto"
                    >
                      <span className="tooltip rounded shadow-lg px-3 py-1 bg-gray-600 text-white -mt-24 text-xs">
                        Stop recording
                      </span>
                      <Image src={'/images/icons/video/stop.svg'} alt="Stop recording" width="100%" height="100%" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="has-tooltip">
                <button
                  onClick={() => setShowCountdown(true)}
                  className="h-10 w-10 mt-2 flex justify-center items-center m-auto"
                >
                  <span className="tooltip rounded shadow-lg px-3 py-1 bg-gray-600 text-white -mt-24 text-xs">
                    Start recording
                  </span>
                  <Image src={'/images/icons/video/record.svg'} alt="Start recording" width="100%" height="100%" />
                </button>
              </div>
            )}
          </div>
          {showCountdown && (
            <RecordTimer
              onFinished={() => {
                setShowCountdown(false);
                handleStartCapture();
              }}
              seconds={5}
            />
          )}
        </div>
      ) : (
        <div className="w-9/12 sm:w-6/12 text-center flex" style={{ height }}>
          <div className="m-auto">
            <div>You can record a 2 minutes video.</div>
            <div className="text-xs w-10/12 m-auto mt-4 text-gray-500">
              You have to enable the camera and microphone permissions to record a video.
            </div>
            <Button size="xs" className="mt-3" onClick={getUserMedia}>
              Enable
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCapture;
